import {css, StyleSheet} from 'aphrodite';
import gql from 'graphql-tag';
import React from 'react';

import isArchived from 'archive/isArchived';
import {StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {ProcedureWithPreconfiguredAnalysisTemplatesFragment} from 'procedures/shared-components/analysis/__generated/ProcedureWithPreconfiguredAnalysisTemplatesFragment';
import {getPowAgnosticPublishedFlowchartVersionFromProcedure} from 'procedures/utils/powAgnosticFlowchartHelpers';
import {disallowsAdhocAnalysis} from 'studies/TypedStudyUtils';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';

const PROCEDURE_METHOD_DEFINITION_VERSION_WITH_ANALYSIS_TEMPLATES_FRAGMENT = gql`
  fragment ProcedureMethodDefinitionVersionWithAnalysisTemplatesFragment on ProcedureMethodDefinitionVersionNode {
    id
    analysisTemplateConfigurations {
      ... on ProcedureAnalysisTemplateConfigurationNode {
        analysisTemplate {
          id
          name
          archiveRecord {
            purpose
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const PROCEDURE_WITH_PRECONFIGURED_ANALYSIS_TEMPLATES_FRAGMENT = gql`
  fragment ProcedureWithPreconfiguredAnalysisTemplatesFragment on ProcedureNode {
    id
    name
    editUrl
    templateCollection {
      id
      name
    }
    currentPublishingRecord {
      ... on PublishingRecordNode {
        id
        publishingStatus
      }
      ...InaccessibleInfoGraphQL
    }
    procedureFlowchart {
      ... on ProcedureFlowchartNode {
        id
        publishedFlowchartVersion {
          id
          nodes {
            ...ProcedureMethodDefinitionVersionWithAnalysisTemplatesFragment
          }
        }
      }
    }
    publishedProcedureFlowchartConfigVersionProxy {
      id
      nodes {
        ...ProcedureMethodDefinitionVersionWithAnalysisTemplatesFragment
      }
    }
  }
  ${PROCEDURE_METHOD_DEFINITION_VERSION_WITH_ANALYSIS_TEMPLATES_FRAGMENT}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export type ProcedureWithAnalysisTemplates = ProcedureWithPreconfiguredAnalysisTemplatesFragment;

export interface PreconfiguredAnalysisMissingTemplatesWarningBannerProps {
  selectedProcedureId: string | null;
  procedureOptions: Array<ProcedureWithAnalysisTemplates>;
  studyType: StudyType;
}

/**
 * Get warning banner if selected assay for study does not allow adhoc analysis & has no analysis templates.
 */
export function PreconfiguredAnalysisMissingTemplatesWarningBanner({
  selectedProcedureId,
  procedureOptions,
  studyType,
}: PreconfiguredAnalysisMissingTemplatesWarningBannerProps): JSX.Element | null {
  if (selectedProcedureId == null || !disallowsAdhocAnalysis(studyType)) {
    return null;
  }
  const procedure: ProcedureWithAnalysisTemplates | undefined = procedureOptions.find(
    (procedure) => procedure.id === selectedProcedureId
  );
  if (procedure == null) {
    return null;
  }
  const publishedFlowchartVersion = getPowAgnosticPublishedFlowchartVersionFromProcedure(procedure);

  if (
    publishedFlowchartVersion != null &&
    !publishedFlowchartVersion.nodes.some(
      (node) =>
        node.analysisTemplateConfigurations.filter(
          (config) => !isInaccessible(config) && !isArchived(config.analysisTemplate)
        ).length > 0
    )
  ) {
    return (
      <div className={css(styles.banner)}>
        <InlineBanner
          bannerType={InlineBannerType.WARNING}
          text="Selected assay does not have analysis templates configured for any of its unit operations."
        />
      </div>
    );
  }
  return null;
}

const styles = StyleSheet.create({
  banner: {
    marginTop: 16,
  },
});
